import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Table } from 'reactstrap';
import Loader from '../components/Loader/Loader';
import { ButtonSearch } from '../components/Buttons/Buttons';
import './ContratoColaborador.css';
import ContratoColaboradorService from "../axios_services/contratocolaborador.service";
import { AppContext } from '../application/provider';

const MACRO_AREAS = [
    { id: '1', label: 'Administración' },
    { id: '2', label: 'Negocios' },
    { id: '3', label: 'Externo' },
    { id: '4', label: 'Gerencia' },
    { id: '5', label: 'Tnp' },
];

const ESTADO_CONTRATO_CONFIG = {
    vencido: { label: 'VENCIDO', clase: 'badge-vencido' },
    por_vencer: { label: 'POR VENCER', clase: 'badge-por-vencer' },
    vigente: { label: 'VIGENTE', clase: 'badge-vigente' },
    sin_fecha: { label: 'SIN FECHA', clase: 'badge-sin-fecha' },
};

const formatFecha = (fecha) => {
    if (!fecha) return '—';
    const [year, month, day] = fecha.split('T')[0].split('-');
    return `${day}/${month}/${year}`;
};

function ContratoColaborador() {
    const [state] = useContext(AppContext);
    const userProfile = Number(state.user?.perfil_id);

    const getAreaInicial = () => {
        const perfil = state.catalogos?.perfiles?.find(p => p.tipo_id === state.user?.perfil_id);
        const macroAreaId = perfil?.macro_area_id?.toString();
        const existe = MACRO_AREAS.find(a => a.id === macroAreaId);
        return existe ? macroAreaId : '2';
    };

    const [todosLosColaboradores, setTodosLosColaboradores] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [filtroArea, setFiltroArea] = useState(getAreaInicial);
    const [filtroEstado, setFiltroEstado] = useState('1');
    const [filtroAlerta, setFiltroAlerta] = useState('');

    const [filtrosActivos, setFiltrosActivos] = useState({
        area_id: getAreaInicial(),
        estado: '1',
        alerta: '',
    });

    const fetchContratos = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await ContratoColaboradorService.getContratosColaboradores();
            setTodosLosColaboradores(response.data?.colaboradores || []);
        } catch (error) {
            console.error('Error al obtener contratos:', error);
            alert('Error al obtener los datos de contratos.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchContratos();
    }, [fetchContratos]);

    const colaboradoresFiltrados = todosLosColaboradores.filter((c) => {
        if (filtrosActivos.area_id && c.macro_area_id?.toString() !== filtrosActivos.area_id) return false;
        if (filtrosActivos.estado === '1' && c.estado?.toString() !== '1') return false;
        if (filtrosActivos.estado === '0' && c.estado?.toString() !== '0') return false;
        if (filtrosActivos.alerta && c.estado_contrato !== filtrosActivos.alerta) return false;
        return true;
    });

    const handleBuscar = async () => {
        setFiltrosActivos({
            area_id: filtroArea,
            estado: filtroEstado,
            alerta: filtroAlerta,
        });
        await fetchContratos();
    };

    const renderBadge = (estadoContrato) => {
        const cfg = ESTADO_CONTRATO_CONFIG[estadoContrato] || ESTADO_CONTRATO_CONFIG.sin_fecha;
        return <span className={`badge-contrato ${cfg.clase}`}>{cfg.label}</span>;
    };

    const renderDiasRestantes = (dias, estadoContrato) => {
        if (dias === null || dias === undefined) return '—';
        if (estadoContrato === 'vencido') return `Venció hace ${Math.abs(dias)} día(s)`;
        return `${dias} día(s)`;
    };

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>VERIFICACIÓN DE CONTRATOS</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <label htmlFor="filtroArea">Área:</label>
                    <select
                        id="filtroArea"
                        value={filtroArea}
                        onChange={(e) => setFiltroArea(e.target.value)}
                        className="form-control"
                    >
                        {MACRO_AREAS.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="campo-fecha">
                    <label htmlFor="filtroEstado">Estado colaborador:</label>
                    <select
                        id="filtroEstado"
                        value={filtroEstado}
                        onChange={(e) => setFiltroEstado(e.target.value)}
                        className="form-control"
                    >
                        <option value="1">Activos</option>
                        <option value="0">Cesados</option>
                    </select>
                </div>

                <div className="campo-fecha">
                    <label htmlFor="filtroAlerta">Estado contrato:</label>
                    <select
                        id="filtroAlerta"
                        value={filtroAlerta}
                        onChange={(e) => setFiltroAlerta(e.target.value)}
                        className="form-control"
                    >
                        <option value="">Todos</option>
                        <option value="vencido">Vencido</option>
                        <option value="por_vencer">Por vencer</option>
                    </select>
                </div>
            </div>

            <ButtonSearch onClick={handleBuscar} isLoading={isLoading} />

            {isLoading ? (
                <Loader />
            ) : (
                <div className="table-container">
                    <Table>
                        <thead>
                            <tr>
                                <th>COLABORADOR</th>
                                <th>DNI</th>
                                <th>FECHA INGRESO</th>
                                <th>TÉRMINO CONTRATO</th>
                                <th>DÍAS RESTANTES</th>
                                <th>ESTADO</th>
                            </tr>
                        </thead>
                        <tbody>
                            {colaboradoresFiltrados.length > 0 ? (
                                colaboradoresFiltrados.map((c) => (
                                    <tr key={c.usuario_id} className={`fila-contrato fila-${c.estado_contrato}`}>
                                        <td>{c.nombre_completo}</td>
                                        <td>{c.dni}</td>
                                        <td>{formatFecha(c.fecha_ingreso)}</td>
                                        <td>{formatFecha(c.fecha_termino_contrato)}</td>
                                        <td className={`col-dias ${c.estado_contrato === 'vencido' ? 'dias-vencido' : ''}`}>
                                            {renderDiasRestantes(c.dias_restantes, c.estado_contrato)}
                                        </td>
                                        <td>{renderBadge(c.estado_contrato)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-gray-500 py-4">
                                        No hay datos disponibles.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
}

export default ContratoColaborador;
